// Phone and desktop notifications on this browser (Web Push): the push-only service worker (public/push-sw.js) and
// this browser's subscription, which the server keeps (POST/DELETE notifications/push/). Pure helpers are in
// utils/notifications.js.
import { rtctApi } from "services/rtctApi";
import { base64UrlToBytes, deviceLabel, pushState, sameKey } from "utils/notifications";

const WORKER = "/push-sw.js";

const standalone = () =>
  (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
  window.navigator.standalone === true;

// "ready", "denied", "install" or "unsupported" (utils/notifications pushState()).
export function pushEnvironment() {
  const nav = window.navigator || {};
  const ios = /iPad|iPhone|iPod/.test(nav.userAgent || "") || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  return pushState({
    serviceWorker: "serviceWorker" in nav,
    pushManager: "PushManager" in window,
    notification: "Notification" in window ? window.Notification.permission : null,
    ios,
    standalone: standalone(),
  });
}

// This browser's subscription, or null (none, or no worker registered yet).
export async function currentSubscription() {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  return registration && registration.pushManager ? registration.pushManager.getSubscription() : null;
}

const body = (subscription) => ({ ...subscription.toJSON(), label: deviceLabel(navigator.userAgent, standalone()) });

// Ask for permission, subscribe with the server's key and hand the subscription to the server. Errors: "blocked"
// (the person or the browser said no) and anything the browser or the server throws.
export async function turnOn(publicKey) {
  const permission = await window.Notification.requestPermission();
  if (permission !== "granted") throw new Error("blocked");
  const registration = await navigator.serviceWorker.register(WORKER);
  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription.options && subscription.options.applicationServerKey, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    });
  }
  await rtctApi.subscribePush(body(subscription));
  return subscription;
}

// Tell the server again (it may have lost it, e.g. a reset database); nothing when there is no subscription.
export async function resend(subscription) {
  if (subscription) await rtctApi.subscribePush(body(subscription));
}

// Stop: the server forgets this browser, and the browser drops its subscription.
export async function turnOff() {
  const subscription = await currentSubscription();
  if (!subscription) return;
  try {
    await rtctApi.unsubscribePush(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}
